import { Link } from 'react-router-dom';
import { LocationDescriptor } from 'history';
import { Theme } from '@emotion/react';
import { Typography } from 'omakase';
import {
  cardBody,
  cardContainer,
  cardLink,
  cardTitleContainer,
  descriptionTxt,
  getBodyVariantFromSize,
  getOverlineVariantFromSize,
  getPrimaryVariantFromSize,
  imageContainer,
} from 'omakase/ProductCard/cardCssDefs';
import { uniqueId } from 'es-toolkit/compat';
import React from 'react';

export type CardSize = 'large' | 'medium' | 'small' | 'xsmall';

export interface CardBaseProps {
  // Text elements
  overline?: string;
  primaryText?: string;
  secondaryText?: string;
  bodyText?: string;

  imageComponent: React.ReactNode;
  footerContent?: React.ReactNode;

  /**
   * Where the product card should link to, which allows for including information on the history state object to use in
   * the link.
   *
   * We factor the prop this way so that the ProductCard linking behavior can be consistent with
   * other link-to mechanisms in the render, while defining that linkTo: value in one and only one place.
   */
  linkTo: LocationDescriptor | string;
  truncatePrimaryTextLines?: number;
  truncateBodyTextLines?: number;
  size: CardSize;

  onFocus?: () => void;
  onBlur?: () => void;
  onClick?: () => void;

  //Miscellaneous props
  includeCardBodyPadding?: boolean;
}

/**
 * Base card element, which is unopiniated regarding what type of content it displays, and is exposed for use through
 * content-type-specific wrappers (e.g., ProductCard, ShopBrandCard)
 */
const CardBase: React.FunctionComponent<CardBaseProps> = ({
  imageComponent,
  primaryText,
  secondaryText,
  linkTo,
  bodyText,
  truncateBodyTextLines = 3,
  truncatePrimaryTextLines,
  size,
  onFocus,
  onBlur,
  overline,
  footerContent,
  onClick,
  includeCardBodyPadding = false,
}) => {
  const titleIdRef = React.useRef<string>(uniqueId('title_'));

  return (
    <div
      css={(theme: Theme) => cardContainer(theme, size)}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onFocus}
      onMouseLeave={onBlur}
    >
      <Link css={cardLink} aria-labelledby={titleIdRef.current} to={linkTo} onClick={onClick} />
      <div css={(theme: Theme) => imageContainer(theme, size)}>{imageComponent}</div>
      <div css={(theme: Theme) => cardBody(theme, { size, includeCardBodyPadding })}>
        <div css={(theme: Theme) => cardTitleContainer(theme, size, { truncatePrimaryTextLines })}>
          {overline && (
            <Typography
              mb={0.5}
              color="fgDefault"
              variant={getOverlineVariantFromSize(size)}
              id={primaryText ? undefined : titleIdRef.current}
            >
              {overline}
            </Typography>
          )}
          {primaryText && (
            <Typography
              mb={0.5}
              color="fgDefault"
              variant={getPrimaryVariantFromSize(size)}
              id={titleIdRef.current}
            >
              {primaryText}
            </Typography>
          )}
          {secondaryText && (
            <Typography color="fgDefault" variant={getBodyVariantFromSize(size)}>
              {secondaryText}
            </Typography>
          )}
        </div>
        {bodyText && (
          <Typography
            color="fgSubtle"
            variant={getBodyVariantFromSize(size)}
            css={descriptionTxt(truncateBodyTextLines)}
          >
            {bodyText}
          </Typography>
        )}
        {footerContent}
      </div>
    </div>
  );
};

export default CardBase;
